/**
 * Admin user-management modal (local auth). Gated on is_admin.
 * Список юзеров + создание + переключение роли + мягкая деактивация.
 */
import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Checkbox,
  Tag,
  Space,
  App,
  Popconfirm,
  Alert,
} from 'antd';
import { UserAddOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  listAdminUsers,
  createAdminUser,
  setAdminUserRole,
  deactivateAdminUser,
  type AdminUser,
} from '../../shared/api/client';
import { useAuthStore } from '../../shared/session/authStore';

interface AdminUsersModalProps {
  open: boolean;
  onClose: () => void;
}

const AdminUsersModal: React.FC<AdminUsersModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const currentUserId = useAuthStore((s) => s.userId);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listAdminUsers());
    } catch {
      setError(t('idea_calc.admin.msg_load_error'));
    } finally {
      setLoading(false);
    }
  };

  // Грузим список при каждом открытии — данные могли измениться извне.
  useEffect(() => {
    if (open) fetchUsers();
  }, [open]);

  const handleCreateUser = async (values: {
    email: string;
    password: string;
    is_admin?: boolean;
  }) => {
    setCreateLoading(true);
    try {
      await createAdminUser({
        email: values.email,
        password: values.password,
        is_admin: values.is_admin || false,
      });
      message.success(t('idea_calc.admin.msg_created'));
      setShowCreateModal(false);
      form.resetFields();
      fetchUsers();
    } catch {
      message.error(t('idea_calc.admin.msg_create_error'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggleAdmin = async (user: AdminUser) => {
    try {
      await setAdminUserRole(user.id, !user.is_admin);
      message.success(t('idea_calc.admin.msg_role_updated'));
      fetchUsers();
    } catch {
      message.error(t('idea_calc.admin.msg_role_error'));
    }
  };

  const handleDeactivate = async (userId: number) => {
    try {
      await deactivateAdminUser(userId);
      message.success(t('idea_calc.admin.msg_deactivated'));
      fetchUsers();
    } catch {
      message.error(t('idea_calc.admin.msg_deactivate_error'));
    }
  };

  const columns: ColumnsType<AdminUser> = [
    { title: t('idea_calc.admin.col_id'), dataIndex: 'id', key: 'id', width: 60 },
    { title: t('idea_calc.admin.col_email'), dataIndex: 'email', key: 'email' },
    {
      title: t('idea_calc.admin.col_role'),
      key: 'role',
      width: 120,
      render: (_, record) =>
        record.is_admin ? (
          <Tag color="blue">{t('idea_calc.admin.role_admin')}</Tag>
        ) : (
          <Tag>{t('idea_calc.admin.role_user')}</Tag>
        ),
    },
    {
      title: t('idea_calc.admin.col_status'),
      key: 'status',
      width: 120,
      render: (_, record) =>
        record.is_active ? (
          <Tag color="green">{t('idea_calc.admin.status_active')}</Tag>
        ) : (
          <Tag color="red">{t('idea_calc.admin.status_inactive')}</Tag>
        ),
    },
    {
      title: t('idea_calc.admin.col_created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString() : '—',
    },
    {
      title: t('idea_calc.admin.col_actions'),
      key: 'actions',
      width: 220,
      render: (_, record) => {
        const isSelf = String(record.id) === currentUserId;
        if (isSelf) return <Tag>{t('idea_calc.admin.you')}</Tag>;
        if (!record.is_active) return null;
        return (
          <Space>
            <Button size="small" onClick={() => handleToggleAdmin(record)}>
              {record.is_admin
                ? t('idea_calc.admin.remove_admin')
                : t('idea_calc.admin.make_admin')}
            </Button>
            <Popconfirm
              title={t('idea_calc.admin.deactivate_confirm_title')}
              description={t('idea_calc.admin.deactivate_confirm_desc')}
              onConfirm={() => handleDeactivate(record.id)}
              okText={t('idea_calc.admin.yes')}
              cancelText={t('idea_calc.admin.no')}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        title={t('idea_calc.admin.title')}
        open={open}
        onCancel={onClose}
        footer={null}
        width={900}
      >
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setShowCreateModal(true)}
          >
            {t('idea_calc.admin.create_user')}
          </Button>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="middle"
        />
      </Modal>

      <Modal
        title={t('idea_calc.admin.modal_title')}
        open={showCreateModal}
        onCancel={() => {
          setShowCreateModal(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateUser}
          initialValues={{ is_admin: false }}
        >
          <Form.Item
            name="email"
            label={t('idea_calc.admin.field_email')}
            rules={[
              { required: true, message: t('idea_calc.admin.email_required') },
              { type: 'email', message: t('idea_calc.admin.email_invalid') },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label={t('idea_calc.admin.field_password')}
            rules={[
              { required: true, message: t('idea_calc.admin.password_required') },
              { min: 8, message: t('idea_calc.admin.password_min') },
            ]}
          >
            <Input.Password placeholder={t('idea_calc.admin.password_min')} />
          </Form.Item>

          <Form.Item name="is_admin" valuePropName="checked">
            <Checkbox>{t('idea_calc.admin.field_is_admin')}</Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setShowCreateModal(false);
                  form.resetFields();
                }}
              >
                {t('idea_calc.admin.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={createLoading}>
                {t('idea_calc.admin.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdminUsersModal;
